// src/screens/TiGoForm.js
import React, { useMemo, useState } from "react";
import { CheckCircle2, FileCheck2, Clock, ArrowLeft } from "lucide-react";

// === Firebase (ajusta la ruta si tu archivo de config difiere) ===
// Debes exportar tu app en algo como src/Firebase-config.ts|js:
//   import { initializeApp } from "firebase/app";
//   export const app = initializeApp({ ...config... });
import { app } from "../../src/Firebase-config";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

const db = getFirestore(app);

/**
 * TiGoForm
 * Formulario de suscripción a TiGo.
 * - Guarda leads en Firestore (colección: "leads") con type: "tigo_subscription".
 * - Mensaje de éxito: Ventas enviará enlace seguro de pago por Stripe.
 * - Instrucciones de acceso: recibirá link, usuario y correo para configurar contraseña (revisar SPAM).
 * - Botón para regresar a la raíz (/).
 */
export default function TiGoForm() {
  const [data, setData] = useState({
    nombre: "",
    apellidos: "",
    telefono: "",
    correo: "",
    direccion: "",
    ciudad: "",
    estado: "",
    cp: "",
  });
  const [errs, setErrs] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!data.nombre.trim()) e.nombre = "Ingresa tu nombre.";
    if (!data.apellidos.trim()) e.apellidos = "Ingresa tus apellidos.";
    if (!data.telefono.trim() || !/^[0-9+\-()\s]{7,}$/.test(data.telefono)) e.telefono = "Teléfono inválido.";
    if (!data.correo.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(data.correo)) e.correo = "Correo inválido.";
    if (!data.direccion.trim()) e.direccion = "Ingresa tu dirección.";
    if (!data.ciudad.trim()) e.ciudad = "Ingresa tu ciudad.";
    if (!data.estado.trim()) e.estado = "Ingresa tu estado.";
    if (!data.cp.trim() || !/^\d{4,6}$/.test(data.cp)) e.cp = "C.P. inválido.";
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const utm = getUtmParams();
      await addDoc(collection(db, "leads"), {
        type: "tigo_subscription",
        ...data,
        createdAt: serverTimestamp(),
        page: typeof window !== "undefined" ? window.location.pathname : "/",
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
        utm,
      });
      setDone(true);
    } catch (err) {
      console.error("Error guardando suscripción TiGo:", err);
    } finally {
      setSending(false);
    }
  };

  const hero = useMemo(
    () => ({
      title: "Suscripción a TiGo",
      desc:
        "Completa tus datos y nuestro equipo de ventas te enviará un enlace de pago seguro por Stripe.",
    }),
    []
  );

  if (done) {
    return (
      <SuccessPanel />
    );
  }

  return (
    <div className="bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100 min-h-screen">
      {/* Header simple con logo */}
      <header className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 pb-6">
        <div className="flex items-center gap-3">
          <img
            src="/media/TiGoLogo.png"
            alt="TiGo"
            className="h-10 w-auto"
            loading="eager"
            decoding="async"
          />
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">{hero.title}</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-300">{hero.desc}</p>
          </div>
        </div>
      </header>

      {/* Formulario */}
      <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl border border-border bg-white shadow-sm p-6 md:p-8 dark:bg-neutral-900">
          <form onSubmit={submit} className="grid gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nombre" value={data.nombre} onChange={(v) => update("nombre", v)} error={errs.nombre} placeholder="Tu nombre" />
              <Field label="Apellidos" value={data.apellidos} onChange={(v) => update("apellidos", v)} error={errs.apellidos} placeholder="Tus apellidos" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Número de Teléfono" value={data.telefono} onChange={(v) => update("telefono", v)} error={errs.telefono} placeholder="55 0000 0000" />
              <Field label="Correo Electrónico" value={data.correo} onChange={(v) => update("correo", v)} error={errs.correo} placeholder="tu@correo.com" />
            </div>

            <Field label="Dirección" value={data.direccion} onChange={(v) => update("direccion", v)} error={errs.direccion} placeholder="Calle, número, colonia" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Ciudad" value={data.ciudad} onChange={(v) => update("ciudad", v)} error={errs.ciudad} placeholder="Ciudad" />
              <Field label="Estado" value={data.estado} onChange={(v) => update("estado", v)} error={errs.estado} placeholder="Estado" />
              <Field label="C.P." value={data.cp} onChange={(v) => update("cp", v)} error={errs.cp} placeholder="00000" />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Clock className="h-4 w-4 animate-pulse" /> Enviando…
                  </>
                ) : (
                  <>
                    <FileCheck2 className="h-4 w-4" /> Enviar solicitud
                  </>
                )}
              </button>

              <a href="/" className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                <ArrowLeft className="h-4 w-4" /> Regresar
              </a>
            </div>

            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              Al enviar tus datos, aceptas ser contactado por nuestro equipo de ventas para concluir tu suscripción.
            </p>
          </form>
        </div>

        {/* Aviso del proceso tras el envío */}
        <div className="mt-6 rounded-2xl border border-dashed border-border p-4 text-sm text-neutral-700 dark:text-neutral-300">
          <p className="font-medium">¿Qué sigue?</p>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            <li>Recibirás un <strong>enlace seguro de pago por Stripe</strong> enviado por el equipo de ventas.</li>
            <li>Te llegará el <strong>link de la plataforma</strong> con tu usuario.</li>
            <li>Recibirás un <strong>correo electrónico</strong> para configurar tu contraseña. <em>Revisa tu carpeta SPAM</em>.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, error }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-1 text-neutral-800 dark:text-neutral-200">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-xl border px-3 py-2 text-sm bg-white dark:bg-white text-neutral-900 dark:text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary/30 ${error ? "border-red-500" : "border-border"}`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function SuccessPanel() {
  return (
    <div className="min-h-screen bg-white text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100 flex items-center">
      <div className="mx-auto max-w-lg px-4 sm:px-6 lg:px-8 text-center">
        <img src="/media/TiGoLogo.png" alt="TiGo" className="mx-auto h-12 w-auto" />
        <div className="mt-6 rounded-3xl border border-border bg-white p-8 shadow-sm dark:bg-neutral-900">
          <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
          <h2 className="mt-4 text-xl font-semibold">¡Solicitud enviada!</h2>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
            Nuestro equipo de ventas te hará llegar un <strong>enlace seguro para pago con Stripe</strong>.
            También recibirás el <strong>link de la plataforma</strong> con tu usuario y un correo para configurar tu contraseña.
            <br />
            <em>Por favor, revisa tu carpeta SPAM.</em>
          </p>
          <a href="/" className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:brightness-110">
            <ArrowLeft className="h-4 w-4" /> Regresar al inicio
          </a>
        </div>
      </div>
    </div>
  );
}

// Utils
function getUtmParams() {
  try {
    const usp = new URLSearchParams(window.location.search);
    const utm = {
      source: usp.get("utm_source") || null,
      medium: usp.get("utm_medium") || null,
      campaign: usp.get("utm_campaign") || null,
      term: usp.get("utm_term") || null,
      content: usp.get("utm_content") || null,
    };
    return Object.fromEntries(Object.entries(utm).filter(([, v]) => v !== null));
  } catch {
    return {};
  }
}
